import { useState } from "react";
import { Banner, Button, Drawer, Field, Input, KeyValues, Modal, Toggle } from "@/shared/rd";
import type { AppConfigType, AppConfigView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { defaultFor, draftProblem, effectiveValue, formatDate, formatValue, isChanged, isOn } from "../lib/configValue";
import { AppConfigAuditPanel } from "./AppConfigAuditPanel";
import { useConfigWrites } from "./useConfigWrites";

type Props = {
  /** The key on show; kept while the drawer slides shut. */
  config: AppConfigView | null;
  open: boolean;
  onClose: () => void;
  /** The key's category, for the crumb. */
  sectionLabel: string;
};

const FORM_ID = "app-config-value-form";

const TYPE_LABEL: Record<AppConfigType, string> = {
  boolean: "appConfigs.typeOnOff",
  integer: "appConfigs.typeWholeNumber",
  string: "appConfigs.typeString",
  url: "appConfigs.typeUrl",
};

/** A key's details: its value (edited here), what kind of key it is, where its value comes from, and its history. */
export function AppConfigDetails({ config, open, onClose, sectionLabel }: Props) {
  if (!config) return null;
  // Keyed, so opening another key starts from that key's value.
  return <DetailsFor key={config.key} config={config} open={open} onClose={onClose} sectionLabel={sectionLabel} />;
}

function DetailsFor({ config, open, onClose, sectionLabel }: Props & { config: AppConfigView }) {
  const t = useTranslations();
  const locale = useLocale();
  const name = (locale === "ar" ? config.nameAr : config.nameEn) || config.key;
  const description = locale === "ar" ? config.descriptionAr : config.descriptionEn;
  const writes = useConfigWrites(config, name);
  const current = effectiveValue(config);
  const [draft, setDraft] = useState(current);
  const [touched, setTouched] = useState(false);
  const [asking, setAsking] = useState<"save" | "reset" | null>(null);

  const isBoolean = config.type === "boolean";
  const isCustom = config.origin === "custom";
  const needsRestart = config.scope === "boot";
  const dirty = draft !== current;
  const problem = isBoolean ? null : draftProblem(config, draft);
  const shownProblem = touched && dirty ? problem : null;

  const problemText =
    shownProblem === "url"
      ? t(keys.appConfigs.invalidUrl)
      : shownProblem === "integer"
        ? t("appConfigs.invalidInteger")
        : shownProblem === "integerRange"
          ? t("appConfigs.invalidIntegerRange", { min: config.min ?? "", max: config.max ?? "" })
          : undefined;

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setTouched(true);
    if (!dirty || problem || writes.isPending) return;
    // A start-up key waits for a restart, so it is confirmed; a live key is written at once with Undo.
    if (needsRestart) setAsking("save");
    else void writes.save(draft, { undo: true });
  };

  const source = isCustom
    ? config.updatedAt
      ? t("appConfigs.sourceSetHereOn", { date: formatDate(config.updatedAt, locale) })
      : t("appConfigs.sourceSetHere")
    : isChanged(config)
      ? config.updatedAt
        ? t("appConfigs.sourceSetHereOn", { date: formatDate(config.updatedAt, locale) })
        : t("appConfigs.sourceSetHere")
      : t("appConfigs.sourceEnvDefault");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      width={480}
      badge="gear"
      crumb={`${t(keys.appConfigs.title)} / ${sectionLabel}`}
      title={name}
      sub={config.key}
      actions={
        <>
          {config.source === "db" ? (
            <Button variant="ghost" size="sm" muted={!isCustom} disabled={writes.isPending} onClick={() => setAsking("reset")}>
              {isCustom ? t("appConfigs.deleteKeyAction") : t(keys.appConfigs.resetAction)}
            </Button>
          ) : null}
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            size="sm"
            disabled={!dirty || writes.resetPending}
            loading={writes.updatePending}
            loadingLabel={t(keys.appConfigs.saving)}
          >
            {t(keys.common.save)}
          </Button>
        </>
      }
    >
      {writes.failure ? (
        <Banner
          tone="error"
          title={t("appConfigs.saveFailedTitle", { name })}
          actions={
            <Button size="sm" loading={writes.isPending} loadingLabel={t(keys.shell.retrying)} onClick={() => void writes.failure?.retry()}>
              {t(keys.shell.retry)}
            </Button>
          }
          onDismiss={writes.clearFailure}
          dismissLabel={t(keys.common.close)}
        >
          {`${writes.failure.message} ${t("appConfigs.nothingChanged")}`}
        </Banner>
      ) : null}
      {needsRestart ? (
        <Banner tone="warning" title={t("appConfigs.needsRestart")}>
          {t("appConfigs.needsRestartTip")}
        </Banner>
      ) : null}
      {description ? (
        <p dir="auto" style={{ margin: 0, fontSize: 14, color: "var(--rd-text-secondary)" }}>
          {description}
        </p>
      ) : null}

      <form id={FORM_ID} onSubmit={submit} noValidate>
        {isBoolean ? (
          <Field
            label={t(keys.appConfigs.addValueLabel)}
            help={isCustom ? t("appConfigs.customHelp") : t("appConfigs.defaultHelp", { value: formatValue(config, defaultFor(config), t) })}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 14 }}>
              <Toggle checked={isOn(draft)} label={name} disabled={writes.isPending} onChange={(on) => setDraft(on ? "true" : "false")} />
              <span aria-hidden="true">{formatValue(config, draft, t)}</span>
            </span>
          </Field>
        ) : (
          <Field
            label={t(keys.appConfigs.addValueLabel)}
            htmlFor="app-config-value"
            help={isCustom ? t("appConfigs.customHelp") : t("appConfigs.defaultHelp", { value: formatValue(config, defaultFor(config), t) })}
            error={problemText}
          >
            <Input
              id="app-config-value"
              name={config.key}
              dir="ltr"
              type={config.type === "integer" ? "number" : config.type === "url" ? "url" : "text"}
              inputMode={config.type === "integer" ? "numeric" : undefined}
              placeholder={config.type === "url" ? "https://" : undefined}
              min={config.min}
              max={config.max}
              value={draft}
              invalid={Boolean(problemText)}
              disabled={writes.isPending}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={() => setTouched(true)}
              style={config.type === "integer" ? { width: 140 } : { width: "100%" }}
            />
          </Field>
        )}
      </form>

      <KeyValues
        rows={[
          { label: t(keys.appConfigs.addTypeLabel), value: t(TYPE_LABEL[config.type]) },
          { label: t("appConfigs.kvRead"), value: needsRestart ? t("appConfigs.readBoot") : t("appConfigs.readLive") },
          { label: t("appConfigs.kvSource"), value: source },
        ]}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--rd-text-meta)" }}>
          {t(keys.appConfigs.showHistory)}
        </span>
        <AppConfigAuditPanel config={config} />
      </div>

      <Modal
        open={asking === "save"}
        onClose={writes.isPending ? undefined : () => setAsking(null)}
        width={440}
        title={t("appConfigs.changeTitle", { name })}
        actions={
          <>
            <Button variant="ghost" muted onClick={() => setAsking(null)} disabled={writes.isPending}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="primary"
              loading={writes.updatePending}
              loadingLabel={t("appConfigs.applying")}
              onClick={async () => {
                await writes.save(draft, { undo: false });
                setAsking(null);
              }}
            >
              {t(keys.appConfigs.confirmAction)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t("appConfigs.changeBodyRestart", { from: formatValue(config, current, t), to: formatValue(config, draft, t) })}
        </p>
      </Modal>

      <Modal
        open={asking === "reset"}
        onClose={writes.isPending ? undefined : () => setAsking(null)}
        width={440}
        title={isCustom ? t("appConfigs.deleteNamedTitle", { name }) : t("appConfigs.resetNamedTitle", { name })}
        actions={
          <>
            <Button variant="ghost" muted onClick={() => setAsking(null)} disabled={writes.isPending}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant={isCustom ? "danger" : "primary"}
              loading={writes.resetPending}
              loadingLabel={isCustom ? t("appConfigs.deleting") : t("appConfigs.resetting")}
              onClick={async () => {
                const ok = await writes.reset();
                setAsking(null);
                if (!ok) return;
                // A deleted key is gone; a reset key now reads its default.
                if (isCustom) onClose();
                else setDraft(defaultFor(config));
              }}
            >
              {isCustom ? t(keys.appConfigs.confirmDeleteAction) : t(keys.appConfigs.confirmResetAction)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {isCustom
            ? t("appConfigs.deleteNamedBody", { key: config.key })
            : t("appConfigs.resetNamedBody", { value: formatValue(config, defaultFor(config), t) })}
        </p>
      </Modal>
    </Drawer>
  );
}
